/**
 * Popup handling shared by the OAuth connect flows (Instantly email, Unipile
 * LinkedIn, HubSpot).
 *
 * A browser only lets a script open a window while it still holds the
 * *transient user activation* granted by a click. Each of these flows first has
 * to ask our server for the provider's auth URL, and that round trip — the
 * backend calling out to Instantly/Unipile — regularly outlives the activation:
 * Chrome drops it after a few seconds, Safari honours a synchronous open only.
 * A `window.open()` placed after that `await` is therefore blocked outright,
 * returning null, on exactly the connections that are a little slower than the
 * developer's.
 *
 * So the window is opened up front, while the click is still ours, and pointed
 * at the auth URL once the server answers. If the browser blocks even that, the
 * user gets a link to click instead — their click is a fresh activation, which
 * no popup blocker stands in the way of.
 */

const placeholderDocument = (message: string) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${message}</title></head>` +
  `<body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;` +
  `font-family:system-ui,-apple-system,'Segoe UI',sans-serif;color:#475569;background:#f8fafc">` +
  `<p>${message}</p></body></html>`;

export interface AuthPopup {
  /**
   * True once the user has closed the sign-in window. Always false when the
   * popup was blocked and the flow continued through the fallback link, since
   * that window is not ours to observe — such flows end on success or timeout.
   */
  isClosed(): boolean;
  /**
   * Send the popup to the provider's auth URL. Resolves false when the user
   * abandoned the flow (closed the window, or dismissed the fallback dialog),
   * meaning the caller should stop rather than poll for a sign-in that will
   * never happen.
   */
  navigate(url: string): Promise<boolean>;
  /** Close the window, if we still hold one. */
  close(): void;
}

/**
 * Open the sign-in window. Call this BEFORE any `await`, straight out of the
 * click handler, or the browser will block it.
 */
export function openAuthPopup(opts: { message?: string; width?: number; height?: number } = {}): AuthPopup {
  const { message = 'Connecting…', width = 600, height = 700 } = opts;

  let win: Window | null = null;
  try {
    win = window.open('', '_blank', `width=${width},height=${height},scrollbars=yes`);
  } catch {
    win = null;
  }

  if (win) {
    // Give the user something to look at while the server fetches the auth URL.
    try {
      win.document.write(placeholderDocument(message));
      win.document.close();
    } catch {
      /* writing to about:blank was refused — the window itself still works */
    }
  }

  return {
    isClosed: () => !!win && win.closed,

    navigate: async (url: string) => {
      if (win) {
        if (win.closed) return false;
        try {
          // replace() keeps about:blank out of the popup's history, so "back"
          // inside the sign-in window can't strand the user on a blank page.
          win.location.replace(url);
          win.focus();
          return true;
        } catch {
          // Never expected on a fresh about:blank, but if the navigation is
          // refused we hand over to the link rather than strand the user.
          win = null;
        }
      }
      return await popupBlockedDialog(url);
    },

    close: () => {
      try {
        win?.close();
      } catch {
        /* already gone */
      }
    },
  };
}

/**
 * Shown when the browser blocked the sign-in window. The primary action is a
 * real anchor: the user's click on it counts as a fresh user activation, so the
 * provider opens even with popups blocked for the site.
 *
 * Resolves true once the user opens the link, false if they dismiss.
 */
function popupBlockedDialog(url: string): Promise<boolean> {
  return new Promise<boolean>(resolve => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    const overlay = document.createElement('div');
    overlay.className = 'modal modal-open';
    overlay.style.zIndex = '10000';

    const titleId = `pb-title-${Math.random().toString(36).slice(2, 9)}`;
    const descId = `pb-desc-${Math.random().toString(36).slice(2, 9)}`;

    const box = document.createElement('div');
    box.className = 'modal-box';
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', titleId);
    box.setAttribute('aria-describedby', descId);

    const h3 = document.createElement('h3');
    h3.id = titleId;
    h3.className = 'text-lg font-bold';
    h3.textContent = 'Your browser blocked the sign-in window';

    const p = document.createElement('p');
    p.id = descId;
    p.className = 'py-4 text-sm';
    p.textContent =
      'Open it manually to continue. To skip this step next time, allow pop-ups for this site in your browser settings.';

    const actions = document.createElement('div');
    actions.className = 'modal-action';

    const cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn-ghost';
    cancelBtn.textContent = 'Cancel';

    const openLink = document.createElement('a');
    openLink.href = url;
    openLink.target = '_blank';
    openLink.rel = 'noopener noreferrer';
    openLink.className = 'btn btn-primary';
    openLink.textContent = 'Open sign-in';

    actions.appendChild(cancelBtn);
    actions.appendChild(openLink);
    box.appendChild(h3);
    box.appendChild(p);
    box.appendChild(actions);

    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop';

    overlay.appendChild(box);
    overlay.appendChild(backdrop);

    const focusables: HTMLElement[] = [cancelBtn, openLink];

    const cleanup = (result: boolean) => {
      document.removeEventListener('keydown', onKeydown, true);
      overlay.remove();
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try {
          previouslyFocused.focus();
        } catch {
          /* ignore */
        }
      }
      resolve(result);
    };

    const onKeydown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        cleanup(false);
        return;
      }
      if (ev.key === 'Tab') {
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (ev.shiftKey && active === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && active === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };

    cancelBtn.addEventListener('click', () => cleanup(false));
    // Let the browser follow the link, then keep polling for the sign-in.
    openLink.addEventListener('click', () => cleanup(true));
    backdrop.addEventListener('click', () => cleanup(false));
    document.addEventListener('keydown', onKeydown, true);

    document.body.appendChild(overlay);

    requestAnimationFrame(() => openLink.focus());
  });
}
